var _a, _b, _c, _d;
import { __decorate, __metadata, __param } from "tslib";
import initialize, { prepareAccess } from '@patsnap-utils/passport';
import { initializeConfig } from './initialize-config';
import { generateExceptionRedirector, psAxios, setPostByFormPassport } from '@patsnap/common-ps-http';
import { ACCOUNT_SERVICE_URI, ACCOUNT_URI, CLIENT_NAME, NODE_ENV } from '@cores/constants/envConfig';
import { createUniformSetting, currentClientUUID } from '@patsnap/uniform-setting';
import getCommonInit from '@cores/utils/getCommonInit';
import defer from '@cores/startup/token-base/defer';
import { checkFreeSupported, injectWorkspaceFreeInterceptor } from '@cores/apis/interceptors/workspace_free.interceptor';
import { handleExcludesUrlRegExps, passportInterceptManager, redirectFn } from '@cores/startup/token-base';
import { PRESETS_TOKEN_BASE_WORKSPACE } from '@cores/startup/token-base/presets';
import { HeaderServiceManager } from '@/domains/header/header-service';
import { inject, injectable } from '../inversify.config';
import { getMessage, i18nSymbol } from '../i18n';
import setTitle from '@/infrastructure/utils/setTitle';
import { initExtendI18n } from '@/infrastructure/locales/i18nInstance';
import { initTransformCodeInterceptor } from '@cores/apis/interceptors/transform_code.interceptor';
import { applySignInterceptors } from '@cores/startup/token-base/sign';
import { UserService } from '@/domains/user/services/user-service.interface';
import { useHeader } from '@/presentation/composables/use-header';
import { FeatureConfig } from '@/domains/feature-config/types/feature-config.interface';
import { UserInfoService } from '@/domains/user/services/user-info-service.interface';
import { setUserInfo } from '@patsnap-utils/ali-arms';
import setWebsiteFont from '@cores/utils/configure-website-font';
const DEFAULT_REDIRECT_URL = `${ACCOUNT_URI}/user-settings/#/no-permission?from_site=${CLIENT_NAME}`;
const supports = ['cn', 'en', 'tw', 'jp'];
export const WorkspaceStartupSymbol = Symbol.for('WorkspaceStartup');
const isProd = process.env.NODE_ENV === 'production';
let WorkspaceStartup = class WorkspaceStartup {
    constructor(i18n, headerServiceManager, userService, userInfoService, featureConfig) {
        this.i18n = i18n;
        this.headerServiceManager = headerServiceManager;
        this.userService = userService;
        this.userInfoService = userInfoService;
        this.featureConfig = featureConfig;
        this.isFreeView = false;
    }
    async init() {
        this.headerService = this.headerServiceManager.getHeaderServiceInstance();
        this.prepareInterceptors();
        this.applySignInterceptors();
        this.passport = await this.prepareToken();
        this.applyJwtHeaderInterceptor(this.passport);
        let _error = null;
        try {
            await prepareAccess();
        }
        catch (e) {
            _error = e;
            console.error('[prepareTokenBase]', e);
        }
        finally {
            /**
             * 此接口除了检查免费模式，对于已经登录的用户后端逻辑中包含建立分享关系的逻辑
             * 因此在无论是否登录都需要调用此接口
             */
            this.isFreeView = await this.checkFreeMode();
            if (this.isFreeView) {
                this.passport?.removeLocal();
                passportInterceptManager.eject();
                injectWorkspaceFreeInterceptor();
            }
            else {
                if (_error) {
                    redirectFn(_error);
                }
            }
        }
        if (!this.isFreeView) {
            setPostByFormPassport(this.passport);
        }
    }
    async initI18n() {
        const locale = this.i18n.locale;
        const message = await getMessage(locale);
        setWebsiteFont(locale);
        this.i18n.setLocaleMessage(locale, message);
        initExtendI18n();
        initTransformCodeInterceptor(this.i18n);
    }
    async initPrepareData() {
        const { setting } = await this.prepareData();
        this.setting = setting;
        this.initThirdParty();
    }
    async prepareToken() {
        const passport = await initialize(initializeConfig);
        return passport;
    }
    async checkFreeMode() {
        return await checkFreeSupported();
    }
    async prepareData() {
        const data = await Promise.all([
            this.initHeader(),
            this.initUniformSetting(),
            this.userService.initUser(),
        ]).then((res) => {
            const [, setting] = res;
            this.userInfoService.updateUserInfoFreeView(this.isFreeView);
            return {
                setting: setting,
            };
        }).catch(generateExceptionRedirector(403, DEFAULT_REDIRECT_URL));
        return data;
    }
    async initHeader() {
        const self = this;
        this.headerService.beforeLocaleChange = (localeKey, done) => {
            self.setting.locale.setLocale(localeKey).then(() => {
                window.location.reload();
                done();
            });
        };
        const { initHeaderData } = useHeader();
        return initHeaderData();
    }
    async initUniformSetting() {
        const setting = createUniformSetting({
            verbose: true,
            axiosInstance: psAxios,
            loggedIn: true,
            baseAccountUrl: ACCOUNT_SERVICE_URI,
            region: this.featureConfig.region.toLowerCase(),
            locale: {
                supports,
            },
        });
        setting.locale.getLocale().then(({ default: defaultLocale, user }) => {
            const locale = user || defaultLocale;
            /* 如果 cookies 中存储的语言和持久化的语言不匹配，则刷新页面 */
            const lang = this.i18n.locale;
            if (lang !== locale) {
                window.location.reload();
            }
        });
        return setting;
    }
    async initCommonData() {
        return await getCommonInit();
    }
    prepareInterceptors() {
        /* 清除 csrfTokenInterceptor */
        psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);
        const { apiVersion, excludesUrlRegExps } = PRESETS_TOKEN_BASE_WORKSPACE;
        // 添加apiVersion
        const interceptorRequestSuccess = config => {
            const excludesUrlConfig = handleExcludesUrlRegExps({
                config,
                excludesUrlRegExps,
                apiVersion,
            });
            if (excludesUrlConfig)
                return excludesUrlConfig;
            return config;
        };
        psAxios.psInterceptors.nodeApiInterceptor = psAxios.interceptors.request.use(interceptorRequestSuccess, null);
        passportInterceptManager.inject();
        const clientUUID = currentClientUUID();
        const locale = this.i18n.locale;
        psAxios.psInterceptors.xFromInterceptor = psAxios.interceptors.request.use(config => {
            config.headers['X-PatSnap-From'] = 'w-analytics-workspace';
            config.headers['X-Site-Lang'] = locale.toUpperCase();
            config.headers['X-Device-ID'] = clientUUID;
            return config;
        }, undefined);
    }
    applySignInterceptors() {
        // @ts-ignore
        window[this.revertString('6csg9md32h')] = () => {
            return this.passport.getToken('token') || '';
        };
        applySignInterceptors(psAxios);
    }
    decodeJwt(token) {
        if (!token) {
            throw new Error('Token not found');
        }
        const strings = token.split('.');
        return JSON.parse(decodeURIComponent(escape(window.atob(strings[1].replace(/-/g, '+').replace(/_/g, '/')))));
    }
    additionalJwtHeaderInterceptor(token) {
        return (config) => {
            try {
                const jwt = this.decodeJwt(token);
                const { sub = '', authorities = [], tenant_id = '', client_id = '' } = jwt;
                config.headers['X-User-ID'] = sub;
                config.headers['X-User-Roles'] = authorities;
                config.headers['X-Tenant-ID'] = tenant_id;
                config.headers['X-Client-ID'] = client_id;
            }
            catch (err) {
                // console.error('Parse jwt error: ', err);
            }
            return config;
        };
    }
    applyJwtHeaderInterceptor(passport) {
        if (NODE_ENV !== 'production') {
            const token = passport.getToken('token');
            psAxios.interceptors.request.use(this.additionalJwtHeaderInterceptor(token));
        }
    }
    initThirdParty() {
        const locale = this.i18n.locale;
        setTitle({ locale, isWhiteLabel: this.headerService.whiteLabel });
        const enableArms = window.location.hostname.includes('zhihuiya.com');
        if (enableArms) {
            setUserInfo({
                patsnapUserId: this.headerService.account?._info?.account_id,
            });
        }
        if (isProd) {
            defer({
                projectName: 'WorkSpace',
                account: this.headerService.account,
                locale: this.i18n.locale,
                useThirdParty: !this.isFreeView,
            });
        }
    }
    revertString(str) {
        return str.split('').reverse().join('');
    }
};
WorkspaceStartup = __decorate([
    injectable(),
    __param(0, inject(i18nSymbol)),
    __param(1, inject(HeaderServiceManager)),
    __param(2, inject(UserService)),
    __param(3, inject(UserInfoService)),
    __param(4, inject(FeatureConfig)),
    __metadata("design:paramtypes", [Object, typeof (_a = typeof HeaderServiceManager !== "undefined" && HeaderServiceManager) === "function" ? _a : Object, typeof (_b = typeof UserService !== "undefined" && UserService) === "function" ? _b : Object, typeof (_c = typeof UserInfoService !== "undefined" && UserInfoService) === "function" ? _c : Object, typeof (_d = typeof FeatureConfig !== "undefined" && FeatureConfig) === "function" ? _d : Object])
], WorkspaceStartup);
export { WorkspaceStartup };
